import { useState, useEffect, useMemo } from 'react';
import { fetchUserCompanies } from '../services/userMembership';

/**
 * @param {string | undefined} userUID
 * @returns {{ companies: Array<{ companyId: string, companyName: string }>, companyOptions: Array<{ label: string, value: string }>, loading: boolean, error: string | null }}
 */
export function useUserCompanies(userUID) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userUID) {
      setCompanies([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchUserCompanies(userUID)
      .then((res) => {
        if (cancelled) return;
        if (res.success) {
          setCompanies(res.data);
        } else {
          setCompanies([]);
          setError(res.error || 'Failed to load companies');
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setCompanies([]);
          setError(e?.message || 'Failed to load companies');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userUID]);

  const companyOptions = useMemo(
    () =>
      companies.map((c) => ({
        label: c.companyName,
        value: c.companyId,
      })),
    [companies]
  );

  return { companies, companyOptions, loading, error };
}
